import { apiBaseUrl } from './apiBaseUrl';

const SESSION_STORAGE_KEY = 'bloom_ads_session_id';
const VISIT_RECORDED_KEY = 'bloom_ads_visit_recorded';
let visitInFlight = false;

function getSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const value = crypto.randomUUID();
    sessionStorage.setItem(SESSION_STORAGE_KEY, value);
    return value;
  } catch {
    return '';
  }
}

export function getAdsContext() {
  const params = new URLSearchParams(window.location.search);
  const landingPage = `${window.location.origin}${window.location.pathname}`;

  return {
    url: window.location.href,
    landingPage,
    referrer: document.referrer,
    gclid: params.get('gclid') || '',
    sessionId: getSessionId(),
  };
}

// Capture attribution now, but send only after initial loading has finished.
export function scheduleAdsVisit() {
  try {
    const context = getAdsContext();
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void recordAdsVisit(context);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') send();
    };
    const schedule = () => {
      if (sent) return;
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(send, { timeout: 2000 });
      } else {
        window.setTimeout(send, 200);
      }
    };

    // Preserve short visits when the user leaves before the idle callback runs.
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  } catch {
    // Tracking must not interrupt application startup.
  }
}

export async function recordAdsVisit(context?: ReturnType<typeof getAdsContext>) {
  if (visitInFlight) return;
  visitInFlight = true;
  try {
    try {
      if (sessionStorage.getItem(VISIT_RECORDED_KEY)) {
        return;
      }
    } catch {
      // Continue without client-side de-duplication when storage is unavailable.
    }

    const body = JSON.stringify(context ?? getAdsContext());
    const endpoint = `${apiBaseUrl}/api/visit-evidence/visits`;

    // This public endpoint needs no cookies. Unlike sendBeacon, fetch lets us
    // omit credentials and confirm delivery before recording session success.
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'omit',
      priority: 'low',
      keepalive: true,
    });
    if (response.ok) {
      try {
        sessionStorage.setItem(VISIT_RECORDED_KEY, '1');
      } catch {
        // Storage restrictions must not affect the application.
      }
    }
  } catch {
    // Analytics must never prevent the application from rendering.
  } finally {
    visitInFlight = false;
  }
}
